import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';


const NavbarContainer = styled.nav<{ scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: ${props => props.scrolled ? '15px 0' : '25px 0'};
  background: ${props => props.scrolled ? props.theme.colors.white : 'transparent'};
  box-shadow: ${props => props.scrolled ? props.theme.shadows.small : 'none'};
  transition: all 0.3s ease;
`;

const NavbarContent = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  @media (max-width: 600px) {
    justify-content: space-between;
    position: relative;
    flex-direction: row;
  }
`;

const LogoWrapper = styled(motion.div)`
  height: 40px;
  min-width: 120px;
  max-width: 160px;
  display: flex;
  align-items: center;
  z-index: 200;
  background: transparent;
  @media (max-width: 600px) {
    height: 32px;
    min-width: 80px;
    max-width: 110px;
    position: static;
    transform: none;
    flex-shrink: 0;
    img {
      width: auto;
      height: 32px;
      max-width: 110px;
      display: block;
    }
  }
`;

const NavLinks = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 280px;
    flex-direction: column;
    background: ${props => props.theme.colors.white};
    padding: 100px 40px;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.4s ease;
    box-shadow: ${props => props.isOpen ? props.theme.shadows.large : 'none'};
    z-index: 101;
  }
`;

const NavLinkRouter = styled(motion.div)<{ scrolled: boolean }>`
  margin: 0 15px;
  font-weight: 500;
  position: relative;
  
  a {
    color: ${props => props.scrolled ? props.theme.colors.dark : props.theme.colors.white};
    transition: color 0.3s ease;
    text-decoration: none;
    position: relative;
    display: flex;
    align-items: center;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 0;
      height: 2px;
      background: ${props => props.scrolled ? props.theme.colors.primary : props.theme.colors.white};
      transition: width 0.3s ease;
    }
    
    &:hover {
      color: ${props => props.scrolled ? props.theme.colors.primary : props.theme.colors.white};
      
      &::after {
        width: 100%;
      }
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin: 15px 0;
    
    a {
      color: ${props => props.theme.colors.dark};
      font-size: 18px;
      
      &::after {
        background: ${props => props.theme.colors.primary};
      }
    }
  }
`;

const DropdownMenu = styled.div<{ scrolled: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${props => props.theme.colors.white};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadows.medium};
  padding: 10px 0;
  min-width: 180px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;
  z-index: 10;
  
  ${NavLinkRouter}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(5px);
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 8px 20px;
  color: ${props => props.theme.colors.dark};
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.light};
    color: ${props => props.theme.colors.primary};
  }
`;

const ChevronIcon = styled(FiChevronDown)`
  margin-left: 5px;
  transition: transform 0.3s ease;
`;

const MobileMenuButton = styled.button<{ scrolled: boolean }>`
  display: none;
  background: none;
  font-size: 28px;
  color: ${props => props.scrolled ? props.theme.colors.dark : props.theme.colors.white};
  transition: color 0.3s ease;
  border: none;
  cursor: pointer;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: block;
    z-index: 102;
    position: absolute;
    left: 20px;
  }
`;

const RegisterButton = styled(motion.div)<{ scrolled: boolean }>`
  margin-left: 15px;
  @media (max-width: 600px) {
    position: static;
    margin-left: 8px;
    flex-shrink: 0;
  }
  
  a {
    display: inline-block;
    background-color: ${props => props.scrolled ? '#DA015C' : '#ffffff'} !important;
    color: ${props => props.scrolled ? '#fff' : '#DA015C'} !important;
    padding: 12px 24px;
    border-radius: 50px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    white-space: nowrap;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${props => props.theme.shadows.small};
      background-color: ${props => props.scrolled ? '#b80149' : '#FFFFFF'};
      color: ${props => props.scrolled ? '#FFFFFF' : '#DA015C'};
    }
    
    .mobile-btn {
      display: none;
    }
    .desktop-btn {
      display: inline;
    }

    @media (max-width: 600px) {
      padding: 10px 16px;
      font-size: 0.9rem;
      .mobile-btn {
        display: inline;
        background-color: ${props => props.scrolled ? '#DA015C' : '#FFFFFF'};
        color: ${props => props.scrolled ? '#FFFFFF' : '#DA015C'};
      }
      .desktop-btn {
        display: none;
      }
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin-top: 20px;
    margin-left: 0;
  }
`;

interface NavbarProps {
  minimal?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ minimal = false }) => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    // Prevent body scroll when menu is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  return (
    <NavbarContainer scrolled={scrolled}>
      <NavbarContent>
        <LogoWrapper
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/">
            <img src={scrolled ? "/images/wassel.svg" : "/images/Waseel-logo2.svg"} alt="Wassel Logo" style={{ 
              height: '100%', 
              width: 'auto', 
              maxWidth: '160px', 
              display: 'block'
            }} />
          </Link>
        </LogoWrapper>

        <RegisterButton 
          scrolled={scrolled}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <a href="#waitlist" onClick={e => {
            e.preventDefault();
            const el = document.getElementById('waitlist');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth' });
            }
          }}>
            <span className="desktop-btn">{window.location.pathname.startsWith('/en') ? 'Sign Up Now' : window.location.pathname.startsWith('/he') ? 'הירשם עכשיו' : 'سجّل الآن'}</span>
            <span className="mobile-btn">{window.location.pathname.startsWith('/en') ? 'Sign Up Now' : window.location.pathname.startsWith('/he') ? 'הירשם עכשיו' : 'سجّل الآن'}</span>
          </a>
        </RegisterButton>
      </NavbarContent>
    </NavbarContainer>
  );
};

const LanguageSwitchButton = styled.button`
  background: none;
  border: none;
  color: #000000;
  font-weight: 700;
  font-size: 1.02rem;
  margin-left: 18px;
  margin-right: 8px;
  padding: 0.4rem 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s;
  &:hover, &:focus {
    color: #DA015C;
    outline: none;
  }
`;

export default Navbar; 